import React, { useState, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DateRangePreset, formatDateForInput, parseDateFromInput } from '@/types/filters';

interface DateRangePickerProps {
  from?: Date;
  to?: Date;
  onChange: (from: Date | null, to: Date | null) => void;
  presets?: DateRangePreset[];
  label?: string;
  error?: string;
  className?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  from,
  to,
  onChange,
  presets = [],
  label,
  error,
  className
}) => {
  const [fromValue, setFromValue] = useState<string>(from ? formatDateForInput(from) : '');
  const [toValue, setToValue] = useState<string>(to ? formatDateForInput(to) : '');
  const [localError, setLocalError] = useState<string>('');

  // Sync with external changes
  useEffect(() => {
    setFromValue(from ? formatDateForInput(from) : '');
  }, [from]);

  useEffect(() => {
    setToValue(to ? formatDateForInput(to) : '');
  }, [to]);

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFromValue(value);

    const fromDate = parseDateFromInput(value);
    const toDate = parseDateFromInput(toValue);

    // Validation: To date must be >= From date
    if (fromDate && toDate && toDate < fromDate) {
      setLocalError('End date must be after or equal to start date');
    } else {
      setLocalError('');
      onChange(fromDate, toDate);
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToValue(value);

    const fromDate = parseDateFromInput(fromValue);
    const toDate = parseDateFromInput(value);

    // Validation: To date must be >= From date
    if (fromDate && toDate && toDate < fromDate) {
      setLocalError('End date must be after or equal to start date');
    } else {
      setLocalError('');
      onChange(fromDate, toDate);
    }
  };

  const handlePresetClick = (preset: DateRangePreset) => {
    const { from: presetFrom, to: presetTo } = preset.getValue();
    setFromValue(formatDateForInput(presetFrom));
    setToValue(formatDateForInput(presetTo));
    setLocalError('');
    onChange(presetFrom, presetTo);
  };

  const handleClear = () => {
    setFromValue('');
    setToValue('');
    setLocalError('');
    onChange(null, null);
  };

  const hasValue = fromValue || toValue;
  const displayError = error || localError;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Preset Buttons */}
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {presets.map((preset, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handlePresetClick(preset)}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      {/* Date Inputs */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="date"
            value={fromValue}
            onChange={handleFromChange}
            className={cn(
              'w-full pl-10 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
              displayError ? 'border-red-300' : 'border-gray-300'
            )}
            placeholder="From date"
          />
        </div>

        <span className="text-gray-500 text-sm font-medium">to</span>

        <div className="flex-1 relative">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="date"
            value={toValue}
            onChange={handleToChange}
            className={cn(
              'w-full pl-10 pr-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
              displayError ? 'border-red-300' : 'border-gray-300'
            )}
            placeholder="To date"
          />
        </div>

        {/* Clear Button */}
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            title="Clear dates"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Error Message */}
      {displayError && (
        <p className="text-xs text-red-600 mt-1">
          {displayError}
        </p>
      )}
    </div>
  );
};
